import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, wsUrl, cid, errMsg, label } from "./api";

const qKey = (id) => `tro_queue_${id}`;
const cKey = (id) => `tro_match_${id}`;
const readQ = (id) => JSON.parse(localStorage.getItem(qKey(id)) || "[]");
const writeQ = (id, q) => localStorage.setItem(qKey(id), JSON.stringify(q));
const withQueued = (id, ev) => [...ev, ...readQ(id).filter((q) => !ev.some((e) => e.client_event_id === q.client_event_id)).map((q) => ({ ...q, id: q.client_event_id, status: "queued", queued: true }))];
const transient = (err) => !err.response || [408, 425, 429].includes(err.response.status) || err.response.status >= 500;

const hasRejected = (id) => JSON.parse(localStorage.getItem(`tro_rejected_${id}`) || "[]").length > 0;

export function useMatch(id, { onMessage } = {}) {
  const [match, setMatch] = useState(null);
  const [events, setEvents] = useState([]);
  const [conn, setConn] = useState("connecting");
  const [queue, setQueue] = useState(() => readQ(id));
  const [rejected, setRejected] = useState(() => JSON.parse(localStorage.getItem(`tro_rejected_${id}`) || "[]"));
  const reject = useCallback((item, msg) => {
    setRejected((r) => {
      const n = [...r, { ...item, error: msg }];
      localStorage.setItem(`tro_rejected_${id}`, JSON.stringify(n));
      return n;
    });
  }, [id]);
  const clearRejected = useCallback(() => { localStorage.removeItem(`tro_rejected_${id}`); setRejected([]); setConn("online"); }, [id]);
  const [tick, setTick] = useState(0);
  const fetchedAt = useRef(Date.now() / 1000);
  const flushing = useRef(false);
  const cb = useRef(onMessage);
  cb.current = onMessage;

  const load = useCallback(async () => {
    try {
      const [m, e] = await Promise.all([api.get(`/matches/${id}`), api.get(`/matches/${id}/events`)]);
      fetchedAt.current = Date.now() / 1000;
      setMatch(m.data);
      setEvents(withQueued(id, e.data));
      localStorage.setItem(cKey(id), JSON.stringify({ match: m.data, events: e.data, at: fetchedAt.current }));
    } catch (err) {
      if (!err.response) {
        const c = JSON.parse(localStorage.getItem(cKey(id)) || "null");
        if (c) {
          fetchedAt.current = c.at;
          setMatch(c.match);
          setEvents(withQueued(id, c.events));
        }
        setConn("offline");
      } else throw err;
    }
  }, [id]);

  const flush = useCallback(async () => {
    if (flushing.current || !readQ(id).length) return;
    flushing.current = true;
    setConn("syncing");
    let synced = 0, blocked = false, failed = 0;
    try {
      for (const item of readQ(id)) {
        try {
          await api.post(`/matches/${id}/events`, { ...item, recorded_offline: true });
          synced++;
        } catch (err) {
          if (transient(err)) {
            blocked = true;
            break;
          }
          failed++;
          reject(item, errMsg(err));
          toast.error(`Offline ${label(item.type)} at ${item.minute + 1}' rejected: ${errMsg(err)}`);
        }
        const left = readQ(id).filter((x) => x.client_event_id !== item.client_event_id);
        writeQ(id, left);
        setQueue(left);
      }
    } finally {
      flushing.current = false;
    }
    if (blocked) {
      setConn(navigator.onLine ? "reconnecting" : "offline");
      return;
    }
    setConn(failed || hasRejected(id) ? "action" : "online");
    if (synced) toast.success(`${synced} offline event${synced > 1 ? "s" : ""} synchronised`);
    await load().catch(() => {});
  }, [id, load, reject]);
  useEffect(() => {
    let sock, timer, alive = true, delay = 1000;
    const connect = () => {
      sock = new WebSocket(wsUrl(`match_id=${id}`));
      sock.onopen = () => {
        delay = 1000;
        setConn(hasRejected(id) ? "action" : "online");
        flush();
        load();
      };
      sock.onmessage = (ev) => {
        const msg = JSON.parse(ev.data);
        if (msg.type === "hello" || msg.type === "pong") return;
        cb.current?.(msg);
        load();
      };
      sock.onclose = () => {
        if (!alive) return;
        setConn(navigator.onLine ? "reconnecting" : "offline");
        timer = setTimeout(connect, delay);
        delay = Math.min(delay * 2, 15000);
      };
    };
    load().catch(() => {});
    connect();
    const ping = setInterval(() => sock?.readyState === 1 && sock.send("ping"), 20000);
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    const retry = setInterval(() => readQ(id).length && navigator.onLine && flush(), 8000);
    const onOnline = () => flush();
    const onOffline = () => setConn("offline");
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      alive = false;
      clearInterval(retry);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      clearTimeout(timer);
      clearInterval(ping);
      clearInterval(t);
      sock?.close();
    };
  }, [id, load, flush]);

  const elapsed = match
    ? match.elapsed + (match.clock?.running ? Math.max(0, Date.now() / 1000 - fetchedAt.current) : 0)
    : 0;

  const record = useCallback(
    async (payload) => {
      const minute = Math.floor(elapsed / 60);
      const body = { client_event_id: cid(), minute, second: Math.floor(elapsed % 60), recorded_at: new Date().toISOString(), ...payload };
      try {
        const { data } = await api.post(`/matches/${id}/events`, body);
        if (readQ(id).length) flush();
        else setConn((c) => (c === "offline" ? "online" : c));
        await load();
        return data;
      } catch (err) {
        if (!transient(err)) throw err;
        const q = [...readQ(id), body];
        writeQ(id, q);
        setQueue(q);
        setEvents((ev) => [...ev, { ...body, id: body.client_event_id, status: "queued", queued: true }]);
        setConn("offline");
        return { event: { ...body, status: "queued" }, queued: true };
      }
    },
    [id, elapsed, load, flush]
  );

  return { match, events, conn, queue, elapsed, tick, load, record, flush, rejected, clearRejected };
}

export function playersOf(match, teamId) {
  const lu = match?.lineups?.find((l) => l.team_id === teamId);
  if (!lu) return { lineup: null, onField: [], bench: [] };
  const P = (pid) => match.players[pid];
  return { lineup: lu, onField: (lu.on_field || []).map(P).filter(Boolean), bench: (lu.subs || []).filter((p) => !(lu.subs_used || []).includes(p)).map(P).filter(Boolean), all: [...lu.starting, ...lu.subs].map(P).filter(Boolean) };
}
